import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// PDF generator using html2canvas and jsPDF for perfect WYSIWYG output
export const generatePDF = async (template, orientation = 'portrait') => {
  // Get the preview content element
  const element = document.getElementById('scenario-preview-content');
  if (!element) {
    alert('Error: Preview content not found. Please make sure the scenario is loaded.');
    return;
  }

  try {
    // Show loading indicator
    const loadingMessage = document.createElement('div');
    loadingMessage.id = 'pdf-loading';
    loadingMessage.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 20px;
      border-radius: 8px;
      z-index: 10000;
      font-family: sans-serif;
    `;
    loadingMessage.textContent = 'Generating PDF...';
    document.body.appendChild(loadingMessage);

    // Clone the content to avoid modifying the original
    const contentClone = element.cloneNode(true);
    
    // Remove page break overlays and positioning elements
    const elementsToRemove = contentClone.querySelectorAll('.page-break, .absolute, [style*="position: absolute"]');
    elementsToRemove.forEach(el => el.remove());

    // Create a temporary container that preserves all original styling
    const tempContainer = document.createElement('div');
    
    // Calculate proper dimensions based on orientation (same as original preview)
    const pageWidthPx = orientation === 'landscape' ? 1056 : 816; // 11" or 8.5" at 96dpi
    
    tempContainer.style.cssText = `
      position: absolute;
      left: -9999px;
      top: 0;
      width: ${pageWidthPx}px;
      background: white;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
    `;
    
    // Get all CSS from the current page and inject it into a style element
    let allCSS = '';
    
    // Get all stylesheets
    const styleSheets = Array.from(document.styleSheets);
    styleSheets.forEach(sheet => {
      try {
        if (sheet.cssRules) {
          Array.from(sheet.cssRules).forEach(rule => {
            // Skip @media print rules that might interfere
            if (!rule.cssText.includes('@media print')) {
              allCSS += rule.cssText + '\n';
            }
          });
        }
      } catch (e) {
        // Skip if we can't access the stylesheet (CORS issues)
      }
    });
    
    // Get inline styles
    const styleElements = document.querySelectorAll('style');
    styleElements.forEach(style => {
      const content = style.textContent;
      if (!content.includes('@media print')) {
        allCSS += content + '\n';
      }
    });
    
    // Create a style element for the temp container with enhanced CSS
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      ${allCSS}
      
      /* Force table styling to ensure borders and colors are visible */
      table {
        border-collapse: collapse !important;
        width: 100% !important;
        border: 1px solid #d1d5db !important;
        font-size: 1rem !important;
        border-radius: 0.5rem !important;
        overflow: hidden !important;
      }
      
      th, td {
        border: 1px solid #d1d5db !important;
        padding: 8px !important;
        vertical-align: middle !important;
        line-height: 1.2 !important;
      }
      
      th {
        background-color: #f3f4f6 !important;
        font-weight: 600 !important;
        text-align: center !important;
        vertical-align: middle !important;
      }
      
      /* Ensure all table cells are properly aligned */
      tbody td {
        vertical-align: middle !important;
        text-align: left !important;
      }
      
      /* Row striping - only for regular data rows */
      tbody tr:not(.bg-indigo-50):not(.bg-purple-50):nth-child(even) {
        background-color: #f9fafb !important;
      }
      
      tbody tr:not(.bg-indigo-50):not(.bg-purple-50):nth-child(odd) {
        background-color: white !important;
      }
      
      /* Header row styling - full width headers */
      tr.bg-indigo-50 {
        background-color: transparent !important;
      }
      
      tr.bg-indigo-50 td {
        background-color: #eef2ff !important;
        color: #3730a3 !important;
        font-weight: bold !important;
        text-align: center !important;
        vertical-align: middle !important;
        font-size: 1.125rem !important;
        padding: 12px !important;
      }
      
      /* Sub-header styling - only the cell with content, not the entire row */
      tr.bg-purple-50 {
        background-color: transparent !important;
      }
      
      tr.bg-purple-50 td {
        background-color: #faf5ff !important;
        color: #581c87 !important;
        font-weight: 600 !important;
        text-align: left !important;
        vertical-align: middle !important;
        font-size: 1rem !important;
        padding: 10px !important;
      }
      
      /* Regular data cells should have proper alignment */
      tbody tr td:first-child {
        text-align: center !important;
        vertical-align: middle !important;
        font-weight: 600 !important;
        width: 3rem !important;
      }
      
      tbody tr td:last-child {
        text-align: center !important;
        vertical-align: middle !important;
        width: 3rem !important;
      }
      
      /* Objective column with custom modules should align properly */
      tbody tr td:nth-child(3) {
        vertical-align: middle !important;
      }
      
      /* All cells should have consistent padding and line height */
      td {
        padding: 8px !important;
        line-height: 1.2 !important;
        font-size: 1rem !important;
        vertical-align: middle !important;
        height: auto !important;
        min-height: 40px !important;
      }
      
      /* Ensure all table rows have consistent minimum height */
      tr {
        min-height: 40px !important;
      }
      
      /* Custom module cells should have no padding since content has its own */
      td .custom-module-container {
        padding: 0.5rem !important;
        margin: 0 !important;
        background: white !important;
        width: 100% !important;
        box-sizing: border-box !important;
      }
      
      /* Ensure custom module rows have proper vertical alignment and no extra padding */
      tr:has(.custom-module-container) td {
        vertical-align: middle !important;
        padding: 0 !important;
        border: 1px solid #d1d5db !important;
      }
      
      /* Sub-header rows containing custom modules should span full width properly */
      tr.bg-purple-50 td:has(.custom-module-container) {
        vertical-align: middle !important;
        padding: 0 !important;
      }
      
      /* Gray background sections */
      .bg-gray-50 {
        background-color: #f9fafb !important;
        border: 1px solid #e5e7eb !important;
        border-radius: 0.375rem !important;
        padding: 0.75rem !important;
      }
      
      /* Grid layouts */
      .grid {
        display: grid !important;
      }
      
      .grid-cols-1 {
        grid-template-columns: repeat(1, minmax(0, 1fr)) !important;
      }
      
      .grid-cols-2 {
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      }
      
      .grid-cols-5 {
        grid-template-columns: repeat(5, minmax(0, 1fr)) !important;
      }
      
      .gap-4 {
        gap: 1rem !important;
      }
      
      /* Custom vitals module styling */
      .custom-module-container {
        padding: 0.5rem !important;
        background: white !important;
      }
      
      .custom-module-container .grid-cols-5 {
        grid-template-columns: repeat(5, minmax(0, 1fr)) !important;
        gap: 0.75rem !important;
      }
      
      /* OPQRST & SAMPLE module styling */
      .grid-cols-1 {
        grid-template-columns: repeat(1, minmax(0, 1fr)) !important;
      }
      
      .grid-cols-2 {
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      }
      
      .grid-cols-12 {
        grid-template-columns: repeat(12, minmax(0, 1fr)) !important;
      }
      
      .col-span-1 {
        grid-column: span 1 / span 1 !important;
      }
      
      .col-span-4 {
        grid-column: span 4 / span 4 !important;
      }
      
      .col-span-7 {
        grid-column: span 7 / span 7 !important;
      }
      
      .gap-1 {
        gap: 0.25rem !important;
      }
      
      .gap-2 {
        gap: 0.5rem !important;
      }
      
      .space-y-2 > * + * {
        margin-top: 0.5rem !important;
      }
      
      .min-h-9 {
        min-height: 2.25rem !important;
      }
      
      /* Responsive grid for OPQRST/SAMPLE */
      @media (min-width: 768px) {
        .md\\:grid-cols-2 {
          grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        }
      }
      
      /* Ensure colors are printed */
      * {
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
    `;
    
    // Copy the cloned content with all original attributes and styling
    contentClone.removeAttribute('id'); // Remove ID to avoid conflicts
    
    // Completely remove all visual container styling - no borders, shadows, or page styling
    contentClone.classList.remove('print-page', 'shadow-lg', 'border', 'border-gray-300', 'bg-white');
    contentClone.style.cssText = `
      background: white !important;
      border: none !important;
      box-shadow: none !important;
      margin: 0 !important;
      padding: 0.25in !important;
      width: 100% !important;
      height: auto !important;
      max-width: none !important;
      min-height: auto !important;
      position: static !important;
    `;
    
    // Create a simple wrapper that fills the page width properly
    const wrapper = document.createElement('div');
    wrapper.style.cssText = `
      background: white;
      width: 100%;
      min-height: 100vh;
      padding: 0;
      margin: 0;
      border: none;
      box-shadow: none;
    `;
    
    wrapper.appendChild(contentClone);
    
    // Append everything to temp container
    tempContainer.appendChild(styleElement);
    tempContainer.appendChild(wrapper);
    document.body.appendChild(tempContainer);

    // Wait longer for layout to settle and fonts to load
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Generate canvas with high quality, targeting just the content area
    const canvas = await html2canvas(contentClone, {
      scale: 2, // High DPI for crisp text
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
      logging: false,
      onclone: (clonedDoc) => {
        // Ensure all styling is preserved in cloned document
        const clonedStyle = clonedDoc.createElement('style');
        clonedStyle.textContent = `
          table { border-collapse: collapse !important; width: 100% !important; font-size: 1rem !important; border-radius: 0.5rem !important; overflow: hidden !important; }
          th, td { border: 1px solid #d1d5db !important; padding: 8px !important; vertical-align: middle !important; line-height: 1.2 !important; min-height: 40px !important; }
          tr { min-height: 40px !important; }
          th { background-color: #f3f4f6 !important; font-weight: 600 !important; text-align: center !important; vertical-align: middle !important; }
          tbody td { vertical-align: middle !important; text-align: left !important; }
          tbody tr:not(.bg-indigo-50):not(.bg-purple-50):nth-child(even) { background-color: #f9fafb !important; }
          tbody tr:not(.bg-indigo-50):not(.bg-purple-50):nth-child(odd) { background-color: white !important; }
          tr.bg-indigo-50 { background-color: transparent !important; }
          tr.bg-indigo-50 td { background-color: #eef2ff !important; color: #3730a3 !important; font-weight: bold !important; text-align: center !important; vertical-align: middle !important; font-size: 1.125rem !important; padding: 12px !important; }
          tr.bg-purple-50 { background-color: transparent !important; }
          tr.bg-purple-50 td { background-color: #faf5ff !important; color: #581c87 !important; font-weight: 600 !important; text-align: left !important; vertical-align: middle !important; font-size: 1rem !important; padding: 10px !important; }
          tbody tr td:first-child { text-align: center !important; vertical-align: middle !important; font-weight: 600 !important; width: 3rem !important; }
          tbody tr td:last-child { text-align: center !important; vertical-align: middle !important; width: 3rem !important; }
          tbody tr td:nth-child(3) { vertical-align: middle !important; }
          .bg-gray-50 { background-color: #f9fafb !important; border: 1px solid #e5e7eb !important; }
          .grid { display: grid !important; }
          .grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)) !important; }
          .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .grid-cols-5 { grid-template-columns: repeat(5, minmax(0, 1fr)) !important; }
          .grid-cols-12 { grid-template-columns: repeat(12, minmax(0, 1fr)) !important; }
          .col-span-1 { grid-column: span 1 / span 1 !important; }
          .col-span-4 { grid-column: span 4 / span 4 !important; }
          .col-span-7 { grid-column: span 7 / span 7 !important; }
          .gap-1 { gap: 0.25rem !important; }
          .gap-2 { gap: 0.5rem !important; }
          .gap-4 { gap: 1rem !important; }
          .space-y-2 > * + * { margin-top: 0.5rem !important; }
          .min-h-9 { min-height: 2.25rem !important; }
          .custom-module-container { padding: 0.5rem !important; background: white !important; margin: 0 !important; width: 100% !important; box-sizing: border-box !important; }
          .custom-module-container .grid-cols-5 { grid-template-columns: repeat(5, minmax(0, 1fr)) !important; gap: 0.75rem !important; }
          td .custom-module-container { vertical-align: middle !important; }
          tr:has(.custom-module-container) td { vertical-align: middle !important; padding: 0 !important; border: 1px solid #d1d5db !important; }
          tr.bg-purple-50 td:has(.custom-module-container) { vertical-align: middle !important; padding: 0 !important; }
          @media (min-width: 768px) { .md\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; } }
          * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; }
        `;
        clonedDoc.head.appendChild(clonedStyle);
      }
    });

    // Clean up temporary container
    document.body.removeChild(tempContainer);

    // PDF dimensions in mm
    const pageWidth = orientation === 'landscape' ? 279.4 : 215.9; // 11" or 8.5"
    const pageHeight = orientation === 'landscape' ? 215.9 : 279.4; // 8.5" or 11"
    const margin = 12.7; // 0.5" margin (reduced from 0.75")
    const contentWidth = pageWidth - (margin * 2);
    const contentHeight = pageHeight - (margin * 2);
    
    // Create PDF
    const pdf = new jsPDF({
      orientation: orientation,
      unit: 'mm',
      format: 'letter'
    });

    // Calculate how many pages we need
    const canvasHeight = canvas.height;
    const canvasWidth = canvas.width;
    
    // Scale to fit page width, then calculate height per page
    const scale = (contentWidth * 2.83465) / canvasWidth; // Convert mm to pixels (96 DPI)
    const scaledHeight = canvasHeight * scale;
    const pageHeightPx = contentHeight * 2.83465; // Convert mm to pixels
    
    let yOffset = 0;
    let pageCount = 0;
    
    while (yOffset < scaledHeight) {
      if (pageCount > 0) {
        pdf.addPage();
      }
      
      // Create a canvas for this page
      const pageCanvas = document.createElement('canvas');
      const pageCtx = pageCanvas.getContext('2d');
      
      // Set canvas size for this page
      pageCanvas.width = canvasWidth;
      pageCanvas.height = Math.min(pageHeightPx / scale, canvasHeight - (yOffset / scale));
      
      // Fill with white background
      pageCtx.fillStyle = '#ffffff';
      pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      
      // Draw the portion of the original canvas for this page
      pageCtx.drawImage(
        canvas,
        0, yOffset / scale, // Source x, y
        canvasWidth, pageCanvas.height, // Source width, height
        0, 0, // Dest x, y
        pageCanvas.width, pageCanvas.height // Dest width, height
      );
      
      // Add this page to PDF
      const pageImgData = pageCanvas.toDataURL('image/png');
      pdf.addImage(
        pageImgData,
        'PNG',
        margin,
        margin,
        contentWidth,
        (pageCanvas.height * scale) / 2.83465 // Convert back to mm
      );
      
      yOffset += pageHeightPx;
      pageCount++;
    }

    // Generate filename
    const filename = `${(template.name || 'scenario').replace(/[^a-z0-9]/gi, '_')}_${orientation}.pdf`;
    
    // Save the PDF
    pdf.save(filename);

    // Remove loading indicator
    document.body.removeChild(loadingMessage);

  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('Error generating PDF. Please try again.');
    
    // Clean up loading indicator if it exists
    const loadingElement = document.getElementById('pdf-loading');
    if (loadingElement) {
      document.body.removeChild(loadingElement);
    }
  }
};
